/**
 * DateRangeSelector Component
 * Allows users to select date ranges for reports with preset options and custom range
 *
 * Story 1.3: Report Date Range Selector Component
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useUrlState } from '../../hooks/useUrlState';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subQuarters,
  subYears,
  format,
  isAfter,
  isValid,
  parseISO,
} from 'date-fns';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { clsx } from 'clsx';

// Date range preset types
export type DateRangePreset =
  | 'this_week'
  | 'this_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_quarter'
  | 'last_year'
  | 'custom';

export interface DateRange {
  startDate: Date;
  endDate: Date;
  preset: DateRangePreset;
}

export interface DateRangeSelectorProps {
  /** Current date range value */
  value: DateRange | null;
  /** Callback when date range changes */
  onChange: (range: DateRange) => void;
  /** Optional className for styling */
  className?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Default preset to use if no value provided */
  defaultPreset?: DateRangePreset;
}

// Preset configurations
const PRESETS: Array<{ id: DateRangePreset; label: string }> = [
  { id: 'this_week', label: 'This Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'this_quarter', label: 'This Quarter' },
  { id: 'this_year', label: 'This Year' },
  { id: 'last_quarter', label: 'Last Quarter' },
  { id: 'last_year', label: 'Last Year' },
  { id: 'custom', label: 'Custom Range' },
];

/**
 * Calculate date range from preset
 */
export function getDateRangeFromPreset(preset: DateRangePreset): { startDate: Date; endDate: Date } {
  const now = new Date();

  switch (preset) {
    case 'this_week':
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }), // Monday
        endDate: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case 'this_month':
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
      };
    case 'this_quarter':
      return {
        startDate: startOfQuarter(now),
        endDate: endOfQuarter(now),
      };
    case 'this_year':
      return {
        startDate: startOfYear(now),
        endDate: endOfYear(now),
      };
    case 'last_quarter':
      const lastQuarter = subQuarters(now, 1);
      return {
        startDate: startOfQuarter(lastQuarter),
        endDate: endOfQuarter(lastQuarter),
      };
    case 'last_year':
      const lastYear = subYears(now, 1);
      return {
        startDate: startOfYear(lastYear),
        endDate: endOfYear(lastYear),
      };
    case 'custom':
    default:
      // Default to this quarter for custom
      return {
        startDate: startOfQuarter(now),
        endDate: endOfQuarter(now),
      };
  }
}

/**
 * Format date range for display
 */
function formatDateRange(startDate: Date, endDate: Date): string {
  const startStr = format(startDate, 'MMM d, yyyy');
  const endStr = format(endDate, 'MMM d, yyyy');
  return `${startStr} - ${endStr}`;
}

/**
 * Get label for preset
 */
function getPresetLabel(preset: DateRangePreset): string {
  return PRESETS.find((p) => p.id === preset)?.label || 'Select Range';
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange,
  className,
  disabled = false,
  defaultPreset = 'this_quarter',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize with default preset if no value
  useEffect(() => {
    if (!value) {
      const defaultRange = getDateRangeFromPreset(defaultPreset);
      onChange({
        ...defaultRange,
        preset: defaultPreset,
      });
    }
  }, [value, defaultPreset, onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomInputs(false);
        setValidationError(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle preset selection
  const handlePresetSelect = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setShowCustomInputs(true);
      // Initialize with current value or defaults
      if (value) {
        setCustomStartDate(format(value.startDate, 'yyyy-MM-dd'));
        setCustomEndDate(format(value.endDate, 'yyyy-MM-dd'));
      }
    } else {
      const range = getDateRangeFromPreset(preset);
      onChange({
        ...range,
        preset,
      });
      setIsOpen(false);
      setShowCustomInputs(false);
      setValidationError(null);
    }
  };

  // Handle custom date range apply
  const handleCustomApply = () => {
    setValidationError(null);

    // Validate dates
    const start = parseISO(customStartDate);
    const end = parseISO(customEndDate);

    if (!isValid(start) || !isValid(end)) {
      setValidationError('Please enter valid dates');
      return;
    }

    if (isAfter(start, end)) {
      setValidationError('Start date must be before end date');
      return;
    }

    onChange({
      startDate: start,
      endDate: end,
      preset: 'custom',
    });
    setIsOpen(false);
    setShowCustomInputs(false);
  };

  // Handle clear/reset
  const handleClear = () => {
    const defaultRange = getDateRangeFromPreset(defaultPreset);
    onChange({
      ...defaultRange,
      preset: defaultPreset,
    });
    setShowCustomInputs(false);
    setValidationError(null);
  };

  // Display text
  const displayText = value
    ? value.preset === 'custom'
      ? formatDateRange(value.startDate, value.endDate)
      : getPresetLabel(value.preset)
    : 'Select Date Range';

  return (
    <div ref={dropdownRef} className={clsx('relative inline-block', className)}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm font-medium',
          'bg-white border-stone-200 text-stone-700',
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-stone-50 hover:border-stone-300',
          isOpen && 'ring-2 ring-primary-500 ring-offset-1'
        )}
      >
        <Calendar className="h-4 w-4 text-stone-400" />
        <span className="max-w-[200px] truncate">{displayText}</span>
        <ChevronDown
          className={clsx(
            'h-4 w-4 text-stone-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 mt-2 w-72 bg-white rounded-lg shadow-lg border border-stone-200',
            'animate-in fade-in-0 zoom-in-95 duration-100'
          )}
        >
          {/* Preset Options */}
          {!showCustomInputs && (
            <div className="p-2">
              <div className="text-xs font-semibold text-stone-400 uppercase tracking-wider px-2 py-1 mb-1">
                Quick Select
              </div>
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                    value?.preset === preset.id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-stone-700 hover:bg-stone-50'
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          {/* Custom Date Inputs */}
          {showCustomInputs && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-stone-700">Custom Range</span>
                <button
                  onClick={() => {
                    setShowCustomInputs(false);
                    setValidationError(null);
                  }}
                  className="p-1 hover:bg-stone-100 rounded"
                >
                  <X className="h-4 w-4 text-stone-400" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {validationError && (
                  <p className="text-xs text-red-600">{validationError}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleCustomApply}
                    className="flex-1 px-3 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomInputs(false);
                      setValidationError(null);
                    }}
                    className="px-3 py-2 border border-stone-200 text-stone-600 text-sm font-medium rounded-lg hover:bg-stone-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer with clear option */}
          {!showCustomInputs && value && value.preset !== defaultPreset && (
            <div className="border-t border-stone-100 p-2">
              <button
                onClick={handleClear}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-stone-500 hover:bg-stone-50 transition-colors"
              >
                Reset to default
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DATE_RANGE_PRESETS: readonly DateRangePreset[] = [
  'this_week',
  'this_month',
  'this_quarter',
  'this_year',
  'last_quarter',
  'last_year',
  'custom',
];

/** `yyyy-MM-dd` as a LOCAL date, or null. */
const parseDay = (raw: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Hook to manage date range state with URL params.
 *
 * It really is in the URL now (`drange`, plus `dfrom`/`dto` for a custom range). Despite its
 * name it used to be plain `useState`, so drilling from a report into the OKR list and coming
 * Back reset the range to the default. A named preset is stored by name and recomputed on
 * load; only a custom range stores dates.
 */
export function useDateRangeParams(defaultPreset: DateRangePreset = 'this_quarter'): {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  dateRangeParams: { startDate: string; endDate: string };
} {
  const [view, setView] = useUrlState(
    { drange: defaultPreset as string, dfrom: '', dto: '' },
    { drange: DATE_RANGE_PRESETS }
  );

  const dateRange: DateRange = useMemo(() => {
    const preset = view.drange as DateRangePreset;
    if (preset === 'custom') {
      const startDate = parseDay(view.dfrom);
      const endDate = parseDay(view.dto);
      if (startDate && endDate) return { startDate, endDate, preset };
      return { ...getDateRangeFromPreset(defaultPreset), preset: defaultPreset };
    }
    return { ...getDateRangeFromPreset(preset), preset };
  }, [view.drange, view.dfrom, view.dto, defaultPreset]);

  const setDateRange = useCallback(
    (range: DateRange) =>
      setView(
        range.preset === 'custom'
          ? {
              drange: 'custom',
              dfrom: format(range.startDate, 'yyyy-MM-dd'),
              dto: format(range.endDate, 'yyyy-MM-dd'),
            }
          : { drange: range.preset, dfrom: '', dto: '' }
      ),
    [setView]
  );

  // Format dates for API params
  const dateRangeParams = {
    startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
    endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
  };

  return {
    dateRange,
    setDateRange,
    dateRangeParams,
  };
}

export default DateRangeSelector;
